import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { authService } from 'services'
import axios from 'axios'

const SLICE_NAME = 'auth'
// Me, myself and I

const initialState = {
	loading: false,
	isInitialized: false,
	userInfo: null as any,
	error: null as any,
}

// Hydrate from localStorage on first load only
const hydratedState = {
	...initialState,
	userInfo: (() => {
		try {
			const stored = localStorage.getItem('userInfo')
			return stored ? JSON.parse(stored) : null
		} catch {
			return null
		}
	})(),
}

const getInitialState = () => {
	const _localUserInfo = localStorage.getItem('userInfo')
	return {
		loading: false,
		isInitialized: false,
		userInfo: _localUserInfo ? JSON.parse(_localUserInfo) : null,
		error: null,
	}
}

function prepConfig(getState) {
	const {
		userLogin: { userInfo },
	} = getState()

	return {
		headers: {
			Authorization: userInfo && userInfo.token ? `Bearer ${userInfo.token}` : '',
		},
	}
}

export const login = createAsyncThunk(
	`${SLICE_NAME}/login`,
	async (payload: { email: string; password: string }, { rejectWithValue }) => {
		try {
			const data = await authService().login(payload)
			if (data) {
				localStorage.setItem('userInfo', JSON.stringify(data))
				return data
			}
			return rejectWithValue({ message: 'Login failed. Please try again.' })
		} catch (error: any) {
			// Remove any stale userInfo on failed login
			localStorage.removeItem('userInfo')
			return rejectWithValue(error)
		}
	},
)

export const getUserDetails = createAsyncThunk(
	`${SLICE_NAME}/getUserDetails`,
	async (id, { getState, rejectWithValue }) => {
		const { userInfo } = getInitialState()

		const config = {
			headers: {
				Authorization: `Bearer ${userInfo.token}`,
			},
		}
		const data = await axios.get(`/api/users/profile`, config).then(res => {
			return res.data
		})

		if (data) {
			localStorage.removeItem('userInfo')
			localStorage.setItem('userInfo', JSON.stringify(data))
			return data
		} else {
			return null
		}
	},
)

export const patchUser = createAsyncThunk(
	`${SLICE_NAME}/patchUser`,
	async (payload: any, { getState, rejectWithValue }) => {
		console.log(`payload: `, payload)

		// debugger

		try {
			const data = await axios
				.patch(`/api/users/patch`, payload, prepConfig(getState))
				.then(res => res.data)

			if (data) {
				localStorage.setItem('userInfo', JSON.stringify(data))

				return data
			} else {
				return null
			}
		} catch (error) {
			console.log(`error: `, error)
			return rejectWithValue('Unable to patch user')
		}
	},
)

export const updateUser = createAsyncThunk(
	`${SLICE_NAME}/updateUser`,
	async (payload: any, { getState, rejectWithValue }) => {
		const user = payload

		console.log(`payload: `, payload)

		const { userInfo } = getInitialState()

		const config = {
			headers: {
				// 'Content-Type': 'application/json',
				Authorization: `Bearer ${userInfo.token}`,
			},
		}

		const data = await axios.put(`/api/users/update`, user, config).then(res => {
			return res.data
		})

		if (data) {
			console.log(`data: `, data)

			localStorage.setItem('userInfo', JSON.stringify(data))
			return data
		} else {
			return null
		}
	},
)

export const registerInvitedUser = createAsyncThunk(
	`${SLICE_NAME}/registerInvitedUser`,
	async (payload: {
		name: string
		firstName: string
		email: string
		password: string
		lastGroup: string
	}) => {
		const { name, firstName, email, password, lastGroup } = payload

		const config = {
			headers: {
				'Content-Type': 'application/json',
			},
		}

		const data = await axios
			.post('/api/users/invited', { name, firstName, email, password, lastGroup }, config)
			.then(res => {
				return res.data
			})

		// The server tells the inviter (MYD-547).
		if (data) {
			localStorage.setItem('userInfo', JSON.stringify(data))

			return data
		} else {
			return null
		}
	},
)

export const registerNewUser = createAsyncThunk(
	`${SLICE_NAME}/registerNewUser`,
	async (payload: { name: string; firstName: string; email: string; password: string }) => {
		const { name, firstName, email, password } = payload

		const config = {
			headers: {
				'Content-Type': 'application/json',
			},
		}

		const data = await axios
			.post('/api/users', { name, firstName, email, password }, config)
			.then(res => {
				return res.data
			})

		// The server tells the inviter (MYD-547).
		if (data) {
			localStorage.setItem('userInfo', JSON.stringify(data))

			return data
		} else {
			return null
		}
	},
)

export const verifyEmail = createAsyncThunk(
	`${SLICE_NAME}/verifyEmail`,
	async (payload: { email: string; verificationCode: string }, { rejectWithValue }) => {
		const { email, verificationCode } = payload

		const config = {
			headers: {
				'Content-Type': 'application/json',
			},
		}

		try {
			const response = await axios.post(
				'/api/users/verify-code',
				{ email, code: verificationCode },
				config,
			)
			const data = response.data

			// Backend returns { success: true, user: { ... }, message: '...' }
			if (data && data.user && data.user.token) {
				localStorage.setItem('userInfo', JSON.stringify(data.user))
				return data.user
			} else {
				return rejectWithValue('Verification failed')
			}
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data?.message || error.message || 'Invalid verification code',
			)
		}
	},
)

export const resendVerificationCode = createAsyncThunk(
	`${SLICE_NAME}/resendVerificationCode`,
	async (payload: { email: string; name?: string; firstName?: string }, { rejectWithValue }) => {
		const { email, name, firstName } = payload

		const config = {
			headers: {
				'Content-Type': 'application/json',
			},
		}

		try {
			const response = await axios.post(
				'/api/users/send-verification-code',
				{ email, name, firstName },
				config,
			)
			return response.data
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data?.message || error.message || 'Failed to resend verification code',
			)
		}
	},
)

// Passwordless magic-link login for trial/marketing accounts. Returns the
// serialized user (with token), same shape as login.
export const magicLogin = createAsyncThunk(
	`${SLICE_NAME}/magicLogin`,
	async (payload: { token: string }, { rejectWithValue }) => {
		const config = { headers: { 'Content-Type': 'application/json' } }
		try {
			const response = await axios.post('/api/users/magic-login', { token: payload.token }, config)
			const data = response.data
			if (data && data.token) {
				localStorage.setItem('userInfo', JSON.stringify(data))
				return data
			}
			return rejectWithValue('This link is invalid or has expired')
		} catch (error: any) {
			localStorage.removeItem('userInfo')
			return rejectWithValue(
				error.response?.data?.message || error.message || 'This link is invalid or has expired',
			)
		}
	},
)

// Convert a trial account into a real (community) account by setting a password.
export const convertTrial = createAsyncThunk(
	`${SLICE_NAME}/convertTrial`,
	async (payload: { password: string }, { getState, rejectWithValue }) => {
		try {
			const response = await axios.post(
				'/api/users/convert',
				{ password: payload.password },
				prepConfig(getState),
			)
			const data = response.data
			if (data && data.token) {
				localStorage.setItem('userInfo', JSON.stringify(data))
				return data
			}
			return rejectWithValue('Conversion failed. Please try again.')
		} catch (error: any) {
			return rejectWithValue(
				error.response?.data?.message || error.message || 'Conversion failed. Please try again.',
			)
		}
	},
)

const authSlice = createSlice({
	name: SLICE_NAME,
	initialState: hydratedState,
	reducers: {
		logout: state => {
			localStorage.removeItem('userInfo')
			state.userInfo = null
			// localStorage.removeItem('companyInfo')
			// localStorage.removeItem('userCompany')
		},
	},

	extraReducers: {
		[login.pending as any]: state => ({
			...state,
			loading: true,
			error: null,
			userInfo: null,
		}),

		[login.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
			isInitialized: true,
			error: null,
		}),
		[login.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			userInfo: null,
			error: action.payload || action.error,
		}),
		[magicLogin.pending as any]: state => ({
			...state,
			loading: true,
			error: null,
			userInfo: null,
		}),
		[magicLogin.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
			isInitialized: true,
			error: null,
		}),
		[magicLogin.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			userInfo: null,
			error: action.payload || action.error,
		}),

		[convertTrial.pending as any]: state => ({ ...state, loading: true, error: null }),
		[convertTrial.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
			error: null,
		}),
		[convertTrial.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			error: action.payload || action.error,
		}),

		[getUserDetails.pending as any]: state => {
			state.isInitialized = false
			state.loading = true
		},

		[getUserDetails.fulfilled as any]: (state, action) => {
			const user = action.payload
			return {
				loading: false,
				isInitialized: true,
				userInfo: user,
			}
		},

		// Preserve the session while the request is in flight. Resetting to
		// initialState nulls `userInfo`, and PrivateRoute treats that as signed
		// out — so saving a profile field, choosing a community group, advancing
		// an onboarding step or finishing the product tour all bounced the user
		// to /login mid-save. A rejected patch left them signed out for good,
		// because nothing restored the session afterwards.
		[patchUser.pending as any]: state => ({ ...state, loading: true, error: null }),

		[patchUser.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
			isInitialized: false,
		}),
		[patchUser.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			error: action.error,
		}),

		// Same reasoning as patchUser.pending above.
		[updateUser.pending as any]: state => ({ ...state, loading: true, error: null }),

		[updateUser.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
			isInitialized: false,
		}),
		[updateUser.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			error: action.error,
		}),

		[registerInvitedUser.pending as any]: () => ({ ...initialState }),

		[registerInvitedUser.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
		}),

		[registerInvitedUser.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			error: action.error,
		}),

		[registerNewUser.pending as any]: () => ({ ...initialState }),

		[registerNewUser.fulfilled as any]: (state, action) => ({
			...state,
			userInfo: action.payload,
			loading: false,
		}),

		[registerNewUser.rejected as any]: (state, action) => ({
			...state,
			loading: false,
			error: action.error,
		}),

		[verifyEmail.pending as any]: state => {
			state.loading = true
		},

		[verifyEmail.fulfilled as any]: (state, action) => {
			state.userInfo = action.payload
			state.loading = false
			state.isInitialized = true
		},

		[verifyEmail.rejected as any]: (state, action) => {
			state.loading = false
			state.error = action.payload || action.error
		},

		[resendVerificationCode.pending as any]: state => {
			state.loading = true
		},

		[resendVerificationCode.fulfilled as any]: state => {
			state.loading = false
		},

		[resendVerificationCode.rejected as any]: (state, action) => {
			state.loading = false
			state.error = action.payload || action.error
		},
	} as any,
})

export const { logout } = authSlice.actions
export default authSlice.reducer
